import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  AnnotationsDTO,
  BasePropertyDTO,
  BooleanPropertyDTO,
  ColorPropertyDTO,
  ContainerDropDTO,
  ContainersDTO,
  DBListPropertyDTO,
  DoublePropertyDTO,
  ExperimentDispensingPropertiesDTO,
  ExperimentDTO,
  ExperimentInfoDTO,
  ExperimentInfoImagingScheduleDTO,
  FloatPropertyDTO,
  IntPropertyDTO,
  IScoreGroupDTO,
  ListEnumDTO,
  ListPropertyDTO,
  LongPropertyDTO,
  PlateDrawingIdentifierDTO,
  PlateIngredientDTO,
  PlatesDTO,
  RegionCaptureProfileDTO,
  SaveExprimentInfoDTO,
  ScoresGroupsDTO,
  ScoresOfWellDropsDTO,
  StringPropertyDTO,
} from 'business/models/Models';
import {
  Annotations,
  ContainerDrop,
  Containers,
  ExperimentDispensingProperties,
  ExperimentInfoDto,
  ExperimentInfoImagingScheduleDto,
  Plate,
  PlateDrawingIdentifiers,
  PlateIngredient,
  Property,
  PropertyType,
  RegionCaptureProfilesDto,
  SaveExperimentInfo,
  ScoresOfWellDrops,
} from 'components/Experiment/constants';

export const getPlainContainerDropList = (
  containerDropList: ContainerDropDTO[],
): ContainerDrop[] => {
  return containerDropList.map((containerDrop) =>
    containerDrop?.toJSON ? containerDrop.toJSON() : containerDrop,
  );
};

export const getPlainPlateScores = (
  plateScores: ScoresOfWellDropsDTO[][],
): ScoresOfWellDrops[][] => {
  if (!plateScores) {
    return [];
  }
  return plateScores.map((scores) =>
    scores.map((score: ScoresOfWellDropsDTO) => (score?.toJSON ? score.toJSON() : score)),
  );
};

export const getPlainExperiment = (experiment: ExperimentDTO): ExperimentDtoClient =>
  experiment.toJSON();

export const getPlainIngredient = (ingredient: PlateIngredientDTO): PlateIngredient =>
  ingredient.toJSON();

export const getPlainPlate = (plate: PlatesDTO): Plate => plate.toJSON();

export const getPlainExperimentSaveInfoResponse = (
  saveExpDto: SaveExprimentInfoDTO,
): SaveExperimentInfo => saveExpDto.toJSON();

export const getPlainDrawingIdentifiers = (
  drawingIdentifiers: AnnotationsDTO,
): Annotations => drawingIdentifiers.toJSON();

export const getPlainImagingSchedules = (
  imagingSchedules: ExperimentInfoImagingScheduleDTO,
): ExperimentInfoImagingScheduleDto => imagingSchedules.toJSON();

export const getPlainExperimentInfo = (
  experimentInfo: ExperimentInfoDTO,
): ExperimentInfoDto => experimentInfo.toJSON();

export const getPlainContainers = (containers: ContainersDTO[]): Containers[] =>
  containers.map((container) => (container.toJSON ? container.toJSON() : container));

export const getPlainRegionCaptureProfileDTO = (
  regionCaptureProfiles: RegionCaptureProfileDTO[],
): RegionCaptureProfilesDto[] => regionCaptureProfiles.map((rcp) => rcp.toJSON());

const getPropertyType = (property) => {
  if (property instanceof BooleanPropertyDTO) {
    return PropertyType.BooleanProperty;
  } else if (property instanceof StringPropertyDTO) {
    return PropertyType.StringProperty;
  } else if (property instanceof DBListPropertyDTO) {
    return PropertyType.DBListProperty;
  } else if (property instanceof ListPropertyDTO) {
    return PropertyType.ListProperty;
  } else if (property instanceof IntPropertyDTO) {
    return PropertyType.IntProperty;
  } else if (property instanceof LongPropertyDTO) {
    return PropertyType.LongProperty;
  } else if (property instanceof ColorPropertyDTO) {
    return PropertyType.ColorProperty;
  } else if (property instanceof DoublePropertyDTO) {
    return PropertyType.DoubleProperty;
  } else if (property instanceof ListEnumDTO) {
    return PropertyType.ListEnum;
  } else if (property instanceof FloatPropertyDTO) {
    return PropertyType.FloatProperty;
  } else {
    return undefined;
  }
};

export const experimentDispensingPropertyProperties = (properties: {
  [key: string]: BasePropertyDTO[];
}): { [key: string]: Property[] } => {
  const plainProperties: { [key: string]: Property[] } = {};
  Object.keys(properties).forEach((key) => {
    plainProperties[key] = properties[key].map((property) => ({
      ...property.toJSON(),
      type: getPropertyType(property),
    }));
  });
  return plainProperties;
};

export const getPlainExperimentDispensingProperties = (
  experimentDispensingProperties: ExperimentDispensingPropertiesDTO,
): ExperimentDispensingProperties => ({
  robots: experimentDispensingProperties.robots.map((robot) => robot.toJSON()),
  properties: experimentDispensingPropertyProperties(
    experimentDispensingProperties.properties,
  ),
});

export const getPlainScoreOptionsList = (
  scoresOptionsList: ScoresGroupsDTO[],
): IScoreGroupDTO[] => scoresOptionsList.map((scoresOptions) => scoresOptions.toJSON());

export const getPlainPlateDrawingIdentifiersList = (
  PlateDrawingIdentifier: PlateDrawingIdentifierDTO[],
): PlateDrawingIdentifiers[] =>
  PlateDrawingIdentifier.map((plateDrawingIdentifier) => plateDrawingIdentifier.toJSON());
